import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'usehooks-ts';

import { Account, Workshop } from '../../api/types';
import InputCopyField from '../../components/form-elements/InputCopyField';

const AccountInputCopyFieldButtonsCard = ({ workshop, account }: { workshop: Workshop; account: Account }) => {
  const [value, copy] = useCopyToClipboard();
  const { t } = useTranslation();
  const userIdTrimmed = workshop?.user_readable_id?.slice(2, workshop?.user_readable_id?.length);
  const portalId = account?.portal?.id;

  const inputCopyFieldButtons = [
    <InputCopyField
      key={0}
      copy={{
        currentCopiedValue: value,
        handleCopy: copy,
      }}
      field={{
        value: userIdTrimmed,
        label: t('workshop:id.label'),
      }}
      aria-label={'customer-id'}
    />,
    <InputCopyField
      key={1}
      copy={{
        currentCopiedValue: value,
        handleCopy: copy,
      }}
      field={{
        value: get(workshop, 'firstname', '-'),
        label: t('workshop:firstname.label'),
      }}
      aria-label={'firstname'}
    />,
    <InputCopyField
      key={2}
      copy={{
        currentCopiedValue: value,
        handleCopy: copy,
      }}
      field={{
        value: get(workshop, 'lastname', '-'),
        label: t('workshop:lastname.label'),
      }}
      aria-label={'lastname'}
    />,
    <InputCopyField
      key={3}
      copy={{
        currentCopiedValue: value,
        handleCopy: copy,
      }}
      field={{
        value: get(workshop, 'email', '-'),
        label: t('workshop:email.label'),
      }}
      aria-label={'email'}
    />,
    <InputCopyField
      key={4}
      copy={{
        currentCopiedValue: value,
        handleCopy: copy,
      }}
      field={{
        value: get(workshop, 'telephone', '-'),
        label: t('workshop:telephone.label'),
      }}
      aria-label={'telephone'}
    />,
    <InputCopyField
      key={5}
      copy={{
        currentCopiedValue: value,
        handleCopy: copy,
      }}
      field={{
        value: get(workshop, 'name', '-'),
        label: t('workshop:name.label'),
      }}
      aria-label={'name'}
    />,
    <InputCopyField
      key={6}
      copy={{
        currentCopiedValue: value,
        handleCopy: copy,
      }}
      field={{
        value: `${get(workshop, 'country', '')}${get(workshop, 'op_vat_number', '-')}`,
        label: t('workshop:vat_number.label'),
      }}
      aria-label={'vat_number'}
    />,
    <InputCopyField
      key={7}
      copy={{
        currentCopiedValue: value,
        handleCopy: copy,
      }}
      field={{
        value: workshop?.language ? t(`location:languages.${workshop?.language.toUpperCase()}`) : '-',
        label: t('workshop:language.label'),
      }}
      aria-label={'language'}
    />,
    <InputCopyField
      key={8}
      copy={{
        currentCopiedValue: value,
        handleCopy: copy,
      }}
      field={{
        value: workshop?.country ? t(`location:countries.${workshop?.country.toUpperCase()}`) : '-',
        label: t('workshop:country.label'),
      }}
      aria-label={'country'}
    />,
    <InputCopyField
      key={9}
      copy={{
        currentCopiedValue: value,
        handleCopy: copy,
      }}
      field={{
        value: get(workshop, 'city', '-'),
        label: t('workshop:city.label'),
      }}
      aria-label={'city'}
    />,
    <InputCopyField
      key={10}
      copy={{
        currentCopiedValue: value,
        handleCopy: copy,
      }}
      field={{
        value: get(workshop, 'zipcode', '-'),
        label: t('workshop:zipcode.label'),
      }}
      aria-label={'zipcode'}
    />,
    <InputCopyField
      key={11}
      copy={{
        currentCopiedValue: value,
        handleCopy: copy,
      }}
      field={{
        value: get(workshop, 'address', '-'),
        label: t('workshop:address.label'),
      }}
      aria-label={'address'}
    />,
  ];

  const portalCopyFieldButtonsMapping = [
    {
      //erwin - vw
      portalId: 1,
      inputCopyFieldButtonsOrder: [0, 1, 2, 5, 11, 9, 10, 8, 3, 4, 6, 7],
    },
    {
      //erwin - audi
      portalId: 2,
      inputCopyFieldButtonsOrder: [0, 1, 2, 5, 11, 9, 10, 8, 3, 4, 6, 7],
    },
    {
      //erwin - skoda
      portalId: 3,
      inputCopyFieldButtonsOrder: [0, 1, 2, 5, 11, 9, 10, 8, 3, 4, 6, 7],
    },
    {
      //erwin - seat
      portalId: 4,
      inputCopyFieldButtonsOrder: [0, 1, 2, 5, 11, 9, 10, 8, 3, 4, 6, 7],
    },
    {
      //bmw - mini
      portalId: 5,
      inputCopyFieldButtonsOrder: [5, 11, 10, 9, 6, 2, 1, 0, 3, 4, 8, 7],
    },
    {
      //ford
      portalId: 7,
      inputCopyFieldButtonsOrder: [8, 0, 1, 2, 5, 11, 9, 10, 4, 3, 6, 7],
    },
    {
      //jlr
      portalId: 8,
      inputCopyFieldButtonsOrder: [8, 1, 2, 11, 9, 10, 0, 5, 6, 3, 4, 7],
    },
    {
      //mazda
      portalId: 9,
      inputCopyFieldButtonsOrder: [8, 5, 6, 11, 9, 10, 1, 2, 0, 3, 4, 7],
    },
    {
      //toyota
      portalId: 10,
      inputCopyFieldButtonsOrder: [8, 1, 2, 5, 6, 11, 9, 10, 0, 3, 4, 7],
    },
    {
      //honda
      portalId: 13,
      inputCopyFieldButtonsOrder: [8, 1, 2, 0, 5, 11, 10, 9, 3, 4, 6, 7],
    },
    {
      //MG
      portalId: 14,
      inputCopyFieldButtonsOrder: [1, 2, 0, 11, 9, 10, 8, 5, 6, 3, 4, 7],
    },
  ];

  const inputCopyFieldButtonsOrder = (portalId: number) => {
    const portal = portalCopyFieldButtonsMapping.find((portal) => portal.portalId === portalId);
    const portalInputCopyFieldButtonsOrder = portal?.inputCopyFieldButtonsOrder;
    if (portalInputCopyFieldButtonsOrder) {
      return [
        inputCopyFieldButtons[portalInputCopyFieldButtonsOrder[0]],
        inputCopyFieldButtons[portalInputCopyFieldButtonsOrder[1]],
        inputCopyFieldButtons[portalInputCopyFieldButtonsOrder[2]],
        inputCopyFieldButtons[portalInputCopyFieldButtonsOrder[3]],
        inputCopyFieldButtons[portalInputCopyFieldButtonsOrder[4]],
        inputCopyFieldButtons[portalInputCopyFieldButtonsOrder[5]],
        inputCopyFieldButtons[portalInputCopyFieldButtonsOrder[6]],
        inputCopyFieldButtons[portalInputCopyFieldButtonsOrder[7]],
        inputCopyFieldButtons[portalInputCopyFieldButtonsOrder[8]],
        inputCopyFieldButtons[portalInputCopyFieldButtonsOrder[9]],
        inputCopyFieldButtons[portalInputCopyFieldButtonsOrder[10]],
        inputCopyFieldButtons[portalInputCopyFieldButtonsOrder[11]],
      ];
    } else {
      return [
        inputCopyFieldButtons[0],
        inputCopyFieldButtons[1],
        inputCopyFieldButtons[2],
        inputCopyFieldButtons[3],
        inputCopyFieldButtons[4],
        inputCopyFieldButtons[5],
        inputCopyFieldButtons[6],
        inputCopyFieldButtons[7],
        inputCopyFieldButtons[8],
        inputCopyFieldButtons[9],
        inputCopyFieldButtons[10],
        inputCopyFieldButtons[11],
      ];
    }
  };
  return <>{inputCopyFieldButtonsOrder(portalId)}</>;
};

export default AccountInputCopyFieldButtonsCard;
